import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../data/base';

// Status mapping da italiano a inglese (identico a ARSCatalogApi)
const statusMapping = {
    "In Pubblicazione": "In Publication",
    "Rev. Cliente": "Client Rev.",
    "Rev. Spaarkly": "Spaarkly Rev.",
    "Rev. Modellista": "Modelist Rev.",
    "Incompleto": "Incomplete",
    "Pubblicato": "Published"
};

// 🎯 NUOVA FUNZIONE: Conta il totale delle SKU di un catalogo (identica a getTotalSkusCount di ARSCatalog)
export const getCatalogueSkusCount = async (catalogueId) => {
    try {
        // Conta tutte le varianti pubblicate di questo catalogo
        const variantsRef = collection(db, "CataloguesVariants");
        const variantsQuery = query(
            variantsRef,
            where("status", "==", "Pubblicato")
        );

        const variantsSnapshot = await getDocs(variantsQuery);

        if (variantsSnapshot.empty) {
            return 0;
        }

        // Conta solo le varianti che appartengono a prodotti di questo catalogo
        let totalSkus = 0;

        // Per ottimizzare, raccogliamo tutti i catalogProductRef unici
        const catalogProductRefs = new Set();
        const variantsByCatalogProduct = {};

        variantsSnapshot.forEach(doc => {
            const variantData = doc.data();
            const catalogProductRef = variantData.catalogProductRef;

            if (catalogProductRef) {
                catalogProductRefs.add(catalogProductRef);

                if (!variantsByCatalogProduct[catalogProductRef]) {
                    variantsByCatalogProduct[catalogProductRef] = [];
                }
                variantsByCatalogProduct[catalogProductRef].push({
                    id: doc.id,
                    ...variantData
                });
            }
        });

        // Verifica quali catalogProductRef appartengono al nostro catalogo
        const catalogProductIds = Array.from(catalogProductRefs);

        // Firestore ha un limite di 10 elementi per le query "in"
        const idChunks = [];
        for (let i = 0; i < catalogProductIds.length; i += 10) {
            idChunks.push(catalogProductIds.slice(i, i + 10));
        }

        for (const chunk of idChunks) {
            const productsQuery = query(
                collection(db, "CataloguesProducts"),
                where("__name__", "in", chunk),
                where("catalogRef", "==", catalogueId)
            );

            const productsSnapshot = await getDocs(productsQuery);

            // Conta le varianti per ogni prodotto trovato
            productsSnapshot.forEach(productDoc => {
                const productVariants = variantsByCatalogProduct[productDoc.id] || [];
                totalSkus += productVariants.length;
            });
        }

        return totalSkus;
    } catch (error) {
        console.error("Error getting catalogue SKUs count:", error);
        throw error;
    }
};

/**
 * Funzione di ricerca server-side ottimizzata con catalogues_variants_map
 * Cerca sui campi:
 * - modelName in CataloguesProducts 
 * - variantName, skuCode, eanCode, upcCode in catalogues_variants_map
 * 
 * @param {string} catalogueId - ID del catalogo in cui cercare
 * @param {string} searchTerm - Termine di ricerca
 * @returns {Promise<Object>} Oggetto con prodotti trovati, varianti specifiche e totale risultati
 */
export const searchCatalogueProductsAndVariants = async (catalogueId, searchTerm) => {
    try {
        if (!searchTerm || searchTerm.trim() === '') {
            return { products: [], matchedVariants: {}, totalResults: 0, totalSkus: 0 };
        }

        const normalizedSearch = searchTerm.toUpperCase().trim();
        const productsRef = collection(db, "CataloguesProducts");

        let foundProducts = new Map(); // Usa Map per evitare duplicati
        let matchedVariants = {}; // Varianti specifiche che hanno fatto match

        // 🎯 NUOVO: Query unificata sui prodotti del catalogo con catalogues_variants_map
        try {
            const productQuery = query(
                productsRef,
                where("catalogRef", "==", catalogueId),
                where("status", "==", "Pubblicato")
            );

            const productSnapshot = await getDocs(productQuery);

            for (const doc of productSnapshot.docs) {
                const productData = doc.data();
                const modelName = (productData.modelName || '').toUpperCase();
                const cataloguesVariantsMap = productData.catalogues_variants_map || [];

                let productMatches = false;
                let specificMatchedVariants = [];

                // Verifica match nel nome del prodotto
                const nameMatch = modelName.includes(normalizedSearch);
                if (nameMatch) {
                    productMatches = true;
                    // Se il prodotto matcha per nome, includi tutte le sue varianti
                    specificMatchedVariants = cataloguesVariantsMap.map(variant => variant.id);
                }

                // Verifica match nelle varianti della mappa
                cataloguesVariantsMap.forEach(variant => {
                    const variantName = (variant.variantName || '').toUpperCase();
                    const skuCode = (variant.skuCode || '').toUpperCase();
                    const eanCode = (variant.eanCode || '').toUpperCase();
                    const upcCode = (variant.upcCode || '').toUpperCase();

                    const variantMatch =
                        variantName.includes(normalizedSearch) ||
                        skuCode.includes(normalizedSearch) ||
                        eanCode.includes(normalizedSearch) ||
                        upcCode.includes(normalizedSearch);

                    if (variantMatch) {
                        productMatches = true;
                        // Se non è già incluso (per evitare duplicati da nome prodotto)
                        if (!specificMatchedVariants.includes(variant.id)) {
                            specificMatchedVariants.push(variant.id);
                        }
                    }
                });

                // Se ha match, aggiungi il prodotto e le varianti specifiche
                if (productMatches) {
                    foundProducts.set(doc.id, { id: doc.id, ...productData });
                    matchedVariants[doc.id] = specificMatchedVariants;
                }
            }
        } catch (error) {
            console.warn('Product search failed:', error);
        }



        // 🎯 NUOVO: Processa i risultati finali con catalogues_variants_map
        const finalProducts = [];
        const productIds = Array.from(foundProducts.keys());

        // Carica i brand in batch per essere più veloce
        const uniqueBrandIds = [...new Set(productIds.map(id => foundProducts.get(id).mainBrandRef).filter(Boolean))];
        const brandCache = {};

        if (uniqueBrandIds.length > 0) {
            try {
                // Carica tutti i brand necessari in parallelo
                const brandPromises = uniqueBrandIds.map(async (brandId) => {
                    try {
                        const brandRef = doc(db, "MainBrands", brandId);
                        const brandSnap = await getDoc(brandRef);
                        return {
                            brandId,
                            brandData: brandSnap.exists() ? brandSnap.data() : { nome_brand: 'N/A' }
                        };
                    } catch (error) {
                        return { brandId, brandData: { nome_brand: 'N/A' } };
                    }
                });

                const brandResults = await Promise.all(brandPromises);
                brandResults.forEach(({ brandId, brandData }) => {
                    brandCache[brandId] = brandData;
                });
            } catch (error) {
                console.warn('Error loading brands in batch:', error);
            }
        }

        // Processa i prodotti con i brand pre-caricati
        let totalSearchSkus = 0;
        productIds.forEach(productId => {
            const productData = foundProducts.get(productId);
            const brandData = brandCache[productData.mainBrandRef] || { nome_brand: 'N/A' };

            finalProducts.push({
                key: productId,
                id: productId,
                modelName: productData.modelName || 'N/A',
                status: statusMapping[productData.status] || productData.status,
                lastUpdate: productData.lastUpdate || 'N/A',
                brandId: productData.mainBrandRef,
                brandName: brandData.nome_brand || brandData.nameBrand || 'N/A',
                imgUrl: productData.urlImage || productData.imgUrl,
                // 🎯 IMPORTANTE: Includi la mappa delle varianti
                catalogues_variants_map: productData.catalogues_variants_map || [],
                list_variants: productData.list_variants || [], // Mantieni per retrocompatibilità
                list_line_tags: productData.list_line_tags || [],
                list_tags: productData.list_tags || [],
                list_size_tags: productData.list_size_tags || [],
                list_frame_color_tags: productData.list_frame_color_tags || [],
                gender: productData.gender,
                modelType: productData.modelType,
                typology: productData.typology,
                mainBrandRef: productData.mainBrandRef,
                age: productData.age || 'adult',
                availableVariants: productData.availableVariants || 0,
                priority: productData.priority || 0,
                isVisible: productData.isVisible !== false
            });

            // Conta le SKU specifiche che hanno fatto match
            const matchedVariantIds = matchedVariants[productId] || [];
            totalSearchSkus += matchedVariantIds.length;
        });

        return {
            products: finalProducts,
            matchedVariants: matchedVariants, // Varianti specifiche che hanno fatto match
            totalResults: foundProducts.size,
            totalSkus: totalSearchSkus,
            isSearchResult: true
        };

    } catch (error) {
        console.error("Error in catalogue server-side search:", error);
        return { products: [], variants: {}, totalResults: 0 };
    }
}; 